<script lang="ts">
  /**
   * One document in a list — Pattern 1's row, at `--row-h`.
   *
   * Shared by Library, In-case and Tax year rather than written three times, which
   * is what keeps "the one tight column is Expires" true on every list instead of
   * on whichever one was edited last.
   *
   * ## Why the whole row is a single button
   *
   * There is no *Open the file* shortcut here. Every route to a file goes through
   * the object page, and that is deliberate: opening a document is written to the
   * audit trail, and the page that records the open is also the page that shows
   * the trail. A shortcut that bypassed it would put entries in a history the
   * person creating them never sees.
   *
   * It also makes the row keyboard-reachable as one target instead of three, and
   * the in-case folder still lands in two taps from the home screen — Vault, then
   * In-case — which is the promise `04-portal-screens.md` actually makes.
   */
  import { Icon, dateZA, zar, DASH } from "@steyncd/steyn-design";
  import { expiryOf, pretty, type Doc } from "../lib/model";

  const { doc, onopen }: { doc: Doc; onopen: (id: string) => void } = $props();

  const exp = $derived(expiryOf(doc.expiresAt));

  /**
   * The provenance line — where this came from and what it says.
   *
   * Assembled from what exists rather than padded with placeholders: a document
   * with no supplier and no date reads "Municipal bill" and nothing else, which is
   * honest. Em dashes belong in a column with a heading, not scattered through a
   * sentence.
   */
  const provenance = $derived(
    [
      pretty(doc.docType),
      doc.supplier,
      doc.policyNumber ? `No. ${doc.policyNumber}` : null,
      doc.amount != null ? zar(doc.amount) : null,
      doc.documentDate ? dateZA(doc.documentDate) : null,
    ]
      .filter(Boolean)
      .join(" · "),
  );
</script>

<button
  class="row"
  class:row--dim={Boolean(doc.replacedBy)}
  id={`doc-${doc.id}`}
  onclick={() => onopen(doc.id)}
>
  <span class="row__glyph" class:row__glyph--case={doc.inCase}>
    <Icon name={doc.inCase ? "shield" : "file"} size={16} />
  </span>

  <span class="row__body">
    <span class="row__name">
      {doc.title}
      {#if doc.replacedBy}<span class="tagline">replaced</span>{/if}
    </span>
    <span class="row__prov">
      {provenance}
      {#if doc.checks.length > 0}
        <!-- A mark, never a score. The sentence saying which check failed is on the
             object page beside the field it failed on; here it is a count, so the
             row stays a row. Glyph and word and colour, all three. -->
        <span class="mark">
          <Icon name="alert" size={11} />
          {doc.checks.length} to check
        </span>
      {/if}
    </span>
  </span>

  <!-- The one tight column. Everything else breathes. -->
  <span class="row__exp" class:row__exp--warn={exp?.tone === "warn"}>
    {#if !exp}
      <!-- Em dash, never blank and never a zero: a document that does not expire is
           a fact, and an empty cell reads as data nobody entered. -->
      <span class="row__dash" aria-hidden="true">{DASH}</span>
      <span class="sr">does not expire</span>
    {:else}
      {#if exp.glyph}<Icon name={exp.glyph} size={12} />{/if}
      <span class="row__word">{exp.word}</span>
      <span class="row__when">{dateZA(exp.at)}</span>
    {/if}
  </span>
</button>

<style>
  .row {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    /* Read from the surface, never hard-coded: `home` sets 52px, and the narrow
       container query relaxes it to `auto` so a phone gets a card rather than a
       squeezed row. A literal 52 here would defeat both. */
    min-height: var(--row-h);
    padding: var(--sp-2) var(--sp-3);
    text-align: left;
    border-bottom: 1px solid var(--line);
    color: var(--tx);
  }
  .row:last-child {
    border-bottom: none;
  }
  .row:hover {
    background: var(--fill);
  }
  .row--dim {
    opacity: 0.55;
  }

  .row__glyph {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    color: var(--mut);
  }
  /* Copper on the in-case documents, because that folder is the one thing on this
     screen you act on when it matters. Never the only signal: the glyph is a
     shield rather than a page. */
  .row__glyph--case {
    color: var(--acc);
  }

  .row__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  /* Serif names. On a home surface `--font-heading` is Newsreader; reading the
     token rather than naming the face means an operator surface would get its own
     without this file changing. */
  .row__name {
    font-family: var(--font-heading);
    font-size: var(--fs-lead);
    font-weight: 600;
    line-height: 1.25;
    color: var(--tx);
    display: flex;
    align-items: baseline;
    gap: var(--sp-2);
    flex-wrap: wrap;
  }
  .tagline {
    font-family: var(--font-body);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .row__prov {
    font-size: var(--fs-small);
    color: var(--mut);
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    flex-wrap: wrap;
  }
  .mark {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--warn);
  }

  .row__exp {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: var(--fs-small);
    color: var(--mut);
    text-align: right;
  }
  .row__exp--warn {
    color: var(--warn);
    font-weight: 700;
  }
  .row__word {
    white-space: nowrap;
  }
  .row__when {
    font-variant-numeric: tabular-nums;
    color: var(--mut);
    white-space: nowrap;
  }
  .row__exp--warn .row__when {
    color: var(--warn);
    opacity: 0.8;
  }
  .row__dash {
    font-size: var(--fs-body);
  }

  .sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* Below the container's narrow threshold the expiry drops under the name rather
     than fighting it for width — a table that scrolls sideways on a phone is the
     failure `03-shell-and-layout.md` names by hand. */
  @container panel (max-width: 560px) {
    .row {
      align-items: flex-start;
      flex-wrap: wrap;
      padding: var(--sp-3);
    }
    .row__exp {
      width: 100%;
      padding-left: calc(16px + var(--sp-3));
      justify-content: flex-start;
      text-align: left;
    }
  }
</style>
